import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import { toastError, toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";

function formatLocalWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function categoryLabel(cat) {
  const map = {
    membership: "Membership",
    settings: "Settings",
    destructive: "Destructive",
    access: "Access",
    security: "Security",
    processing: "Processing",
  };
  return map[cat] || cat;
}

/**
 * Settings → Audit log. Business+ modules.audit for view/export; Free/Pro see upsell.
 * Default timeline omits Access; opt-in via checkbox. Times are viewer local; CSV is UTC.
 */
const OrgAuditLogPanel = ({ orgId, accessToken }) => {
  const [entLoading, setEntLoading] = useState(true);
  const [modules, setModules] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeAccess, setIncludeAccess] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  useEffect(() => {
    if (!orgId || !accessToken) return;
    let cancelled = false;
    (async () => {
      setEntLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/entitlements`, {
          headers: authHeaders(),
        });
        if (!cancelled) setModules(res.data?.modules || {});
      } catch (err) {
        console.error(err);
        if (!cancelled) setModules({});
      } finally {
        if (!cancelled) setEntLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, accessToken, authHeaders]);

  const auditEnabled = Boolean(modules?.audit);

  const loadEvents = useCallback(
    async ({ append = false, beforeId = null } = {}) => {
      if (!orgId || !accessToken || !auditEnabled) return;
      setLoading(true);
      setError(null);
      try {
        const params = {
          limit: 50,
          include_access: includeAccess ? "1" : "0",
        };
        if (beforeId != null) params.before_id = beforeId;
        if (from) params.from = from.length === 16 ? `${from}:00` : from;
        if (to) params.to = to.length === 16 ? `${to}:00` : to;
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/audit`, {
          headers: authHeaders(),
          params,
        });
        const list = Array.isArray(res.data?.events) ? res.data.events : [];
        setEvents((prev) => (append ? [...prev, ...list] : list));
        setNextBeforeId(res.data?.next_before_id ?? null);
      } catch (err) {
        console.error(err);
        if (
          toastEntitlementOrError(err, "Failed to load audit log", {
            canManageSettings: true,
            orgId,
          })
        ) {
          setError(null);
        } else {
          setError(err.response?.data?.message || "Failed to load audit log");
        }
        if (!append) setEvents([]);
      } finally {
        setLoading(false);
      }
    },
    [orgId, accessToken, auditEnabled, includeAccess, from, to, authHeaders]
  );

  // Initial + when Access filter changes (date range applied via button).
  useEffect(() => {
    if (!auditEnabled) return;
    loadEvents({ append: false });
    // intentionally omit from/to so typing dates does not refetch until Apply
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditEnabled, includeAccess]);

  const handleExport = async () => {
    if (!orgId || !accessToken) return;
    setExporting(true);
    try {
      const params = {
        include_access: includeAccess ? "1" : "0",
      };
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axios.get(`${API_BASE_URL}/org/${orgId}/audit/export`, {
        headers: authHeaders(),
        params,
        responseType: "blob",
      });
      const truncated = res.headers?.["x-circa-audit-truncated"] === "1";
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `circa-audit-${orgId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (truncated) {
        toastSuccess("Export downloaded (truncated to 10,000 rows). Narrow the date range for a full export.");
      } else {
        toastSuccess("Audit CSV exported");
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 429) {
        toastError(err, "Export rate limit reached. Try again later.");
      } else {
        toastEntitlementOrError(err, "Failed to export audit log", {
          canManageSettings: true,
          orgId,
        });
      }
    } finally {
      setExporting(false);
    }
  };

  if (entLoading) {
    return (
      <div className="max-w-3xl">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!auditEnabled) {
    return (
      <div className="max-w-3xl space-y-6">
        <div>
          <h3 className="text-base font-semibold">Audit log</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Review membership, settings, and security events for this workplace.
            Supports security reviews and admin oversight — Circa does not claim
            SOC 2, GDPR, or FERPA certification.
          </p>
        </div>
        <div className="rounded-lg border border-border/80 bg-muted/30 px-4 py-4 text-sm text-muted-foreground space-y-3">
          <p>
            Viewing and exporting the audit log requires{" "}
            <span className="font-medium text-foreground">Business</span> or
            higher. Events are still recorded on Free and Pro so history is
            ready when you upgrade.
          </p>
          <p>
            See{" "}
            <Link
              to="/about"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              About Circa
            </Link>{" "}
            for plan overview, then contact Circa to change this workplace’s
            plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold">Audit log</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Membership, settings, destructive, security, and processing events.
            Access events (downloads, profile views) are opt-in below. Times
            shown in your local timezone. Supports security reviews and admin
            oversight — not a compliance certification.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={exporting}
          onClick={handleExport}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="audit-from" className="text-xs">
            From (UTC)
          </Label>
          <Input
            id="audit-from"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-[11.5rem]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="audit-to" className="text-xs">
            To (UTC)
          </Label>
          <Input
            id="audit-to"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-[11.5rem]"
          />
        </div>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input
            type="checkbox"
            checked={includeAccess}
            onChange={(e) => setIncludeAccess(e.target.checked)}
            className="rounded border-border"
          />
          Include access events
        </label>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => loadEvents({ append: false })}
          disabled={loading}
        >
          Apply
        </Button>
      </div>

      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : null}

      {loading && events.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">No events in this range.</p>
      ) : (
        <ol className="relative space-y-0 border-l border-border ml-2">
          {events.map((ev) => {
            const open = expandedId === ev.id;
            return (
              <li key={ev.id} className="relative pl-5 pb-5 last:pb-0">
                <span
                  className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-muted-foreground/40"
                  aria-hidden
                />
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <time className="text-xs text-muted-foreground tabular-nums">
                    {formatLocalWhen(ev.created_at)}
                  </time>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {categoryLabel(ev.category)}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-foreground">{ev.summary}</p>
                <p className="text-xs text-muted-foreground">
                  {ev.actor_display_name || "System"}
                  {ev.target_label ? ` · ${ev.target_label}` : ""}
                </p>
                <button
                  type="button"
                  className="mt-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => setExpandedId(open ? null : ev.id)}
                >
                  {open ? "Hide details" : "Details"}
                </button>
                {open ? (
                  <dl
                    className={cn(
                      "mt-2 space-y-1 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs"
                    )}
                  >
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground shrink-0">Action</dt>
                      <dd className="font-mono">{ev.action}</dd>
                    </div>
                    {ev.target_type ? (
                      <div className="flex gap-2">
                        <dt className="text-muted-foreground shrink-0">Target</dt>
                        <dd>
                          {ev.target_type}
                          {ev.target_id != null ? ` #${ev.target_id}` : ""}
                        </dd>
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground shrink-0">IP</dt>
                      <dd className="break-all">{ev.ip || "—"}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground shrink-0">User-Agent</dt>
                      <dd className="break-all">{ev.user_agent || "—"}</dd>
                    </div>
                    {ev.metadata ? (
                      <div className="flex gap-2">
                        <dt className="text-muted-foreground shrink-0">Meta</dt>
                        <dd className="font-mono break-all">
                          {JSON.stringify(ev.metadata)}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {nextBeforeId != null ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => loadEvents({ append: true, beforeId: nextBeforeId })}
        >
          {loading ? "Loading…" : "Load more"}
        </Button>
      ) : null}
    </div>
  );
};

export default OrgAuditLogPanel;
